'use client';

import CartAddSvg from '@/assets/svgs/cart-add.svg';
import DollarCoinFilledSvg from '@/assets/svgs/dollar-coin-filled.svg';
import SpotlightSvg from '@/assets/svgs/spotlight.svg';
import ContactListsSvg from '@/assets/svgs/main/contact-lists.svg';
import JobsSvg from '@/assets/svgs/main/jobs.svg';
import { selectTransactionByDebitId } from '@/containers/Accounts/redux/transactions.selectors';
import { useFetchAccountDetails } from '@/containers/Accounts/api';
import { Modals } from '@/containers/Modals/all-modals';
import ModalContainer from '@/containers/Modals/ModalContainer';
import ModalHeader from '@/containers/Modals/ModalHeader';
import {
  DebitTransactionDetailsModalParams,
  ModalProps,
} from '@/containers/Modals/types';
import useModals from '@/containers/Modals/useModals';
import { useAppSelector } from '@/lib/redux/store';
import { selectCurrentAccountId } from '@/lib/redux/app.selectors';
import DetailsCard from '@/shared/components/DetailsCard';
import classNames from 'classnames';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const modalName = Modals.DebitTransactionDetailsModal;

interface DebitTransactionDetailsModalProps extends ModalProps {
  params?: DebitTransactionDetailsModalParams;
}

// Helper function to format currency
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    value
  );
};

// Helper function to format number or return dash
const formatValue = (value: number | undefined): string => {
  return value !== undefined ? value.toLocaleString() : '-';
};

const DebitTransactionDetailsModal = ({
  className,
  params,
  variant = 'narrow',
}: DebitTransactionDetailsModalProps) => {
  const { isModalOpen, onClose } = useModals({
    modalName,
    shouldCloseOnEscape: true,
  });

  const { debitId } = params || {};
  const currentAccountId = useSelector(selectCurrentAccountId);
  const { data: accountDetails } = useFetchAccountDetails(
    currentAccountId || undefined,
    !!currentAccountId
  );
  const hasRateplan = !!accountDetails?.data?.p2p_rateplan;

  // Get transaction from Redux
  const transaction = useAppSelector((state) =>
    debitId ? selectTransactionByDebitId(state, debitId) : null
  );

  // Check if transaction is a debit type (for TypeScript)
  const debitTransaction = useMemo(() => {
    if (!transaction || transaction.type !== 'debit') return null;
    return transaction;
  }, [transaction]);

  if (!isModalOpen) return null;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={variant}
        onClose={onClose}
        shouldCloseOnBackgroundClick>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="url"
            icon={<DollarCoinFilledSvg className="h-5 w-5" />}
            title={
              debitTransaction ? `Debit ${debitTransaction.formatted_date}` : 'Loading...'
            }
          />

          {/* Loading State */}
          {!debitTransaction && (
            <div className="flex flex-1 items-center justify-center">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}

          {/* Content */}
          {debitTransaction && (
            <div className="flex flex-1 overflow-hidden">
              {/* Main Panel */}
              <div className="flex flex-1 flex-col overflow-y-auto p-6">
                {/* Transaction Amount - Prominent Display */}
                <div className="mb-6">
                  <div className="text-neutral mb-2 text-lg font-medium dark:text-gray-400">
                    Total Debit Amount
                  </div>
                  <div className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(debitTransaction.amount)}
                  </div>
                  {debitTransaction.notes && (
                    <div className="mt-3 pt-3">
                      <div className="mb-1 text-sm text-gray-500">Notes</div>
                      <div className="text-sm text-gray-900">
                        {debitTransaction.notes}
                      </div>
                    </div>
                  )}
                </div>

                {/* Contacts Section */}
                <div className="mb-6">
                  <DetailsCard
                    icon={<ContactListsSvg className="h-5 w-5" />}
                    title="Contacts">
                    <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                      <span className="text-sm text-gray-500">Contacts Loaded:</span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.leads_loaded)}
                      </span>
                    </div>
                  </DetailsCard>
                </div>

                {/* Conversations Section */}
                <div className="mb-6">
                  <DetailsCard
                    icon={<JobsSvg className="h-5 w-5" />}
                    title="Conversations">
                    <div className="space-y-4">
                      {/* SMS Subsection */}
                      <div>
                        <div className="mb-3 text-xs font-semibold tracking-wider text-gray-400 uppercase">
                          SMS
                        </div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                          <span className="text-sm text-gray-500">SMS Outbounds:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p)}
                          </span>

                          <span className="text-sm text-gray-500">
                            SMS Outbound Attempts:
                          </span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_attempts)}
                          </span>

                          <span className="text-sm text-gray-500">SMS Inbounds:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            -
                          </span>

                          <span className="text-sm text-gray-500">Unregistered SMS:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_unregistered)}
                          </span>
                        </div>
                      </div>

                      {/* MMS Subsection */}
                      <div className="border-t border-gray-300 pt-4 dark:border-gray-800">
                        <div className="mb-3 text-xs font-semibold tracking-wider text-gray-400 uppercase">
                          MMS
                        </div>
                        <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                          <span className="text-sm text-gray-500">MMS Outbounds:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_mmss)}
                          </span>

                          <span className="text-sm text-gray-500">
                            MMS Outbound Attempts:
                          </span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_mms_attempts)}
                          </span>

                          <span className="text-sm text-gray-500">Unregistered MMS:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_mms_unregistered)}
                          </span>

                          <span className="text-sm text-gray-500">Video MMS:</span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_video_mmss)}
                          </span>

                          <span className="text-sm text-gray-500">
                            Video MMS Attempts:
                          </span>
                          <span className="text-right font-mono text-sm text-gray-900">
                            {formatValue(debitTransaction.p2p_video_mms_attempts)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </DetailsCard>
                </div>

                {/* Spotlight Section */}
                {hasRateplan &&
                  (debitTransaction.p2p_rateplan_adjusted ||
                    debitTransaction.p2p_mms_rateplan_adjusted) && (
                    <div className="mb-6">
                      <DetailsCard
                        icon={<SpotlightSvg className="h-5 w-5" />}
                        title="Spotlight Adjustments">
                        <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                          {debitTransaction.p2p_rateplan_adjusted !== undefined && (
                            <>
                              <span className="text-sm text-gray-500">
                                SMS Spotlight Adjusted:
                              </span>
                              <span className="text-successful-600 text-right font-mono text-sm font-semibold">
                                {formatValue(debitTransaction.p2p_rateplan_adjusted)}
                              </span>
                            </>
                          )}

                          {debitTransaction.p2p_mms_rateplan_adjusted !== undefined && (
                            <>
                              <span className="text-sm text-gray-500">
                                MMS Spotlight Adjusted:
                              </span>
                              <span className="text-successful-600 text-right font-mono text-sm font-semibold">
                                {formatValue(debitTransaction.p2p_mms_rateplan_adjusted)}
                              </span>
                            </>
                          )}
                        </div>
                      </DetailsCard>
                    </div>
                  )}

                {/* Surcharges Section */}
                <div>
                  <DetailsCard
                    icon={<CartAddSvg className="h-5 w-5" />}
                    title="Surcharges">
                    <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
                      <span className="text-sm text-gray-500">CV Submissions:</span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.cv_submissions)}
                      </span>

                      <span className="text-sm text-gray-500">Initial Canvasses:</span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.p2p_initial_canvasses)}
                      </span>

                      <span className="text-sm text-gray-500">
                        High Demand Initial Canvasses:
                      </span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.p2p_high_demand_initial_canvasses)}
                      </span>

                      <span className="text-sm text-gray-500">Tracked Links:</span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.tracked_links)}
                      </span>

                      <span className="text-sm text-gray-500">Tracked Link Clicks:</span>
                      <span className="text-right font-mono text-sm text-gray-900">
                        {formatValue(debitTransaction.tracked_link_clicks)}
                      </span>
                    </div>
                  </DetailsCard>
                </div>
              </div>
            </div>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default DebitTransactionDetailsModal;
